'use client'

import * as React from 'react'
import { useState } from 'react'

import { useModalContext } from '@/components/atomic/dialog/modal-context'
import Icon, { IconName } from '@/components/atomic/icon'
import {
  BodySubtext,
  BodyXS,
  TextVariant,
  variantStyles,
} from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

import {
  Select as BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from './base'

export interface SelectOption {
  label: string | React.ReactNode
  fullLabel?: string | React.ReactNode
  value: string | number
  subtext?: string | React.ReactNode
  rightContent?: string | React.ReactNode
}

export interface SelectProps {
  options: SelectOption[]
  value?: string | number
  defaultValue?: string | number | React.ReactNode
  onValueChange?: (value: string) => void
  placeholder?: string | React.ReactNode
  disabled?: boolean
  className?: string
  contentClassName?: string
  itemClassName?: string
  wrapperClassName?: string
  triggerWrapperClassName?: string
  removeIcon?: boolean
  label?: string
  size?: 'small' | 'large'
  required?: boolean
  hideSearch?: boolean
  error?: boolean
  errorMessage?: string
  modal?: boolean
  customEmptyMessage?: string | React.ReactNode
  onSearchChange?: (search: string) => void
  slotProps?: {
    label?: string
    icon?: React.ReactNode
    side?: 'left' | 'right' | 'both'
    showSeparator?: boolean
    separatorHeight?: string
    size?: 'small' | 'large'
    className?: string
  }
  onOpenChange?: (open: boolean) => void
  ignoreClientSideFiltering?: boolean
  /** Custom className for the inner command component (cmdk-root element) */
  commandClassName?: string
  /** Custom max height for the dropdown. Uses default max-h-60 if not specified */
  dropdownMaxHeight?: string
  /** Custom className for the scrollable group container (where max-height is applied) */
  groupClassName?: string
  /** Show a clear button when a value is selected */
  clearable?: boolean
  /** Callback when the clear button is clicked */
  onClear?: () => void
  /**
   * Accessible name for the trigger button (maps to `aria-label`). Prefer this
   * over the placeholder as a handle: the placeholder isn't rendered once a
   * value is selected, whereas the aria-label is stable regardless of value.
   */
  ariaLabel?: string
}

/**
 * @deprecated Use `Select` from `@moment/design-system-ui/select` instead.
 */
export function Select({
  options,
  value: controlledValue,
  defaultValue,
  onValueChange,
  placeholder = 'Select...',
  disabled,
  className,
  contentClassName,
  itemClassName,
  wrapperClassName,
  triggerWrapperClassName,
  label,
  size = 'small',
  required = false,
  removeIcon = false,
  hideSearch = false,
  slotProps,
  error,
  errorMessage,
  modal = false,
  customEmptyMessage,
  onSearchChange,
  onOpenChange,
  ignoreClientSideFiltering = false,
  commandClassName,
  dropdownMaxHeight,
  groupClassName,
  clearable = false,
  onClear,
  ariaLabel,
}: SelectProps) {
  const { insideModal } = useModalContext()
  const [internalValue, setInternalValue] = useState<string | undefined>(
    defaultValue?.toString(),
  )

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  const currentValue = controlledValue?.toString() ?? internalValue
  const selectedOption = options.find(
    (opt) => opt.value.toString() === currentValue,
  )

  // Build dynamic content className with user-provided overrides
  const dynamicContentClassName = [
    '!min-w-[var(--radix-popover-trigger-width)]', // Sets the width of the dropdown to the width of the trigger
    contentClassName, // Allow additional custom classes
  ]
    .filter(Boolean)
    .join(' ')

  // Build dynamic group className - this is where the max-height should be applied
  const dynamicGroupClassName = [
    dropdownMaxHeight, // Only apply if user provides it, otherwise use default max-h-60
    'overflow-auto', // Ensure scrolling works
    'scrollbar-hide', // Hide scrollbar
    groupClassName, // Allow additional custom classes
  ]
    .filter(Boolean)
    .join(' ')

  // Get text variant styles for search input - matches the size prop
  const textVariant =
    size === 'small' ? TextVariant.BodySubtext : TextVariant.BodySmall
  const searchTextStyles = variantStyles[textVariant]

  return (
    <div className={wrapperClassName}>
      {label && (
        <div className={'mb-1'}>
          <BodyXS color="text-moment-blue-grey-800 dark:text-moment-grey-400">
            {label}
            {required && <span className="text-moment-red-700">*</span>}
          </BodyXS>
        </div>
      )}
      <BaseSelect
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        removeIcon={removeIcon}
        hideSearch={hideSearch}
        modal={modal || insideModal}
        onOpenChange={onOpenChange}
      >
        <div>
          <SelectTrigger
            className={cn(
              className,
              size === 'small' && 'h-[28px]',
              size === 'large' && 'h-[32px]',
            )}
            slotProps={slotProps}
            error={error}
            triggerWrapperClassName={triggerWrapperClassName}
            aria-label={ariaLabel}
          >
            {selectedOption?.rightContent ? (
              <div
                className="flex grow items-center justify-between gap-2"
                // Subtract chevron width and margin
                style={{ width: 'calc(100% - 16px - 8px)' }}
              >
                <div className="flex overflow-hidden w-full">
                  {!React.isValidElement(
                    selectedOption?.label ?? placeholder,
                  ) ? (
                    <BodySubtext
                      className="truncate"
                      color={disabled ? 'text-tertiary' : undefined}
                    >
                      {selectedOption?.label ?? placeholder}
                    </BodySubtext>
                  ) : (
                    (selectedOption?.label ?? placeholder)
                  )}
                </div>
                <div className="shrink-0">
                  {!React.isValidElement(selectedOption?.rightContent) ? (
                    <BodySubtext
                      color={disabled ? 'text-tertiary' : 'text-secondary'}
                    >
                      {selectedOption?.rightContent}
                    </BodySubtext>
                  ) : (
                    selectedOption?.rightContent
                  )}
                </div>
              </div>
            ) : (
              <>
                {!React.isValidElement(selectedOption?.label ?? placeholder) ? (
                  <BodySubtext
                    className="truncate"
                    color={disabled ? 'text-tertiary' : undefined}
                  >
                    {selectedOption?.label ?? placeholder}
                  </BodySubtext>
                ) : (
                  (selectedOption?.label ?? placeholder)
                )}
                {clearable && selectedOption && !disabled && (
                  // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      onClear?.()
                      handleValueChange('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onClear?.()
                        handleValueChange('')
                      }
                    }}
                    className="ml-auto mr-1 p-0.5 rounded hover:bg-moment-black-hover dark:hover:bg-moment-white-hover transition-colors cursor-pointer"
                    aria-label="Clear selection"
                  >
                    <Icon
                      iconName={IconName.XMarkIcon}
                      size="small"
                      className="text-secondary"
                    />
                  </div>
                )}
              </>
            )}
          </SelectTrigger>

          {error && errorMessage && (
            <BodyXS color={'text-moment-red-600 mt-1'}>{errorMessage}</BodyXS>
          )}
        </div>

        <SelectContent
          className={dynamicContentClassName}
          placeholder="Search..."
          options={options}
          customEmptyMessage={customEmptyMessage}
          onSearchChange={onSearchChange}
          ignoreClientSideFiltering={ignoreClientSideFiltering}
          commandClassName={commandClassName}
          groupClassName={dynamicGroupClassName}
          searchClassName={searchTextStyles}
        >
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value.toString()}
              displayValue={option.fullLabel ?? option.label}
              subtext={option.subtext}
              rightContent={option.rightContent}
              size={size}
              className={itemClassName}
            />
          ))}
        </SelectContent>
      </BaseSelect>
    </div>
  )
}
